import { ApiResponse, SearchParams } from './types';
import { Method, fetchBlob, fetchJson, toRequestOptions } from './api';
import {
  APIScopeData,
  SearchFieldsResult,
  SearchResult,
  SortOrder,
  QueryBuilderQuery,
} from '@ardoq/api-types';

const toQueryString = ({
  from = 0,
  size = 100,
  sortOrder = SortOrder.DEFAULT,
  sortBy = '',
  branchId = null,
  snapshot,
}: SearchParams): string => {
  const params = new URLSearchParams({
    from: from.toString(),
    size: size.toString(),
    order: sortOrder === SortOrder.DEFAULT ? '' : sortOrder,
    sort: sortBy,
  });
  if (branchId) {
    params.set('branchId', branchId);
  }
  if (snapshot !== undefined) {
    params.set('snapshot', snapshot.toString());
  }

  return params.toString();
};

const defaultParams = { from: 0, size: 100 };

const search = <T>(
  query: QueryBuilderQuery,
  searchParams: SearchParams = defaultParams,
  abortSignal?: AbortSignal
): ApiResponse<SearchResult<T>> => {
  const queryString = toQueryString(searchParams);
  const request = new Request(
    `/api/advanced-search?${queryString}`,
    toRequestOptions(Method.POST, query, { signal: abortSignal })
  );
  return fetchJson<SearchResult<T>>(request);
};

const searchGraphlake = <T>(
  query: QueryBuilderQuery,
  searchParams: SearchParams = defaultParams,
  abortSignal?: AbortSignal
): ApiResponse<SearchResult<T>> => {
  const queryString = toQueryString(searchParams);
  const request = new Request(
    `/api/graphlake/advanced-search?${queryString}`,
    toRequestOptions(Method.POST, query, { signal: abortSignal })
  );
  return fetchJson<SearchResult<T>>(request);
};

const fieldSearch = (
  query: QueryBuilderQuery,
  { branchId }: Pick<SearchParams, 'branchId'> = {}
): ApiResponse<SearchFieldsResult> => {
  const queryString = branchId ? `?branchId=${branchId}` : '';
  const request = new Request(
    `/api/advanced-search-fields${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchJson<SearchFieldsResult>(request);
};

const fieldSearchGraphlake = (
  query: QueryBuilderQuery,
  { branchId, snapshot }: Pick<SearchParams, 'branchId' | 'snapshot'> = {}
): ApiResponse<SearchFieldsResult> => {
  const params = new URLSearchParams();
  if (branchId) params.set('branchId', branchId);
  if (snapshot) params.set('snapshot', snapshot.toString());
  const queryString = params.toString();

  const request = new Request(
    `/api/graphlake/advanced-search-fields?${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchJson<SearchFieldsResult>(request);
};

const exportSearch = async (
  query: QueryBuilderQuery,
  searchParams: SearchParams = defaultParams
): ApiResponse<Blob> => {
  const queryString = toQueryString(searchParams);
  const request = new Request(
    `/api/advanced-search-export?${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchBlob(request);
};

type FetchScopeDataArgs =
  | {
      componentIds: string[];
      referenceIds?: string[];
      minimal?: boolean;
    }
  | {
      query: QueryBuilderQuery;
      minimal?: boolean;
    };

const scopeData = (
  payload: FetchScopeDataArgs,
  abortSignal?: AbortSignal
): ApiResponse<APIScopeData> => {
  const request = new Request(
    '/api/advanced-search-scopedata',
    toRequestOptions(Method.POST, payload, { signal: abortSignal })
  );
  return fetchJson(request);
};

export const advancedSearchApi = {
  search,
  searchGraphlake,
  fieldSearch,
  fieldSearchGraphlake,
  exportSearch,
  scopeData,
};
